import { csvGenerate } from "json-to-csv-export/lib/esm/generate.js";

const DEFAULT_MIME_TYPE = "application/octet-stream";
const CSV_MIME_TYPE = "text/csv;charset=utf-8;";

// Mirrors EXTENSION_MIME_TYPES in the backend platform_api/exports.py so the
// frontend can fall back to inferring a content type when one isn't supplied.
export const EXTENSION_MIME_TYPES: Record<string, string> = {
  csv: "text/csv",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pdf: "application/pdf",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  zip: "application/zip",
};

export function mimeTypeForFilename(filename: string): string {
  const ext = filename.includes(".")
    ? filename.split(".").pop()?.toLowerCase()
    : undefined;
  return (ext && EXTENSION_MIME_TYPES[ext]) || DEFAULT_MIME_TYPE;
}

/**
 * Standardized export file descriptor returned by export tasks over the
 * websocket (and by the sync HTTP response).
 */
export type ExportFileEntry = {
  filename: string;
  feature?: string;
  mimetype?: string;
  /** Exact S3 object key when several exports share the same display name. */
  storage_key?: string;
};

export function exportFileDownloadQuery(
  file: ExportFileEntry,
): { storage_key: string } | undefined {
  return file.storage_key ? { storage_key: file.storage_key } : undefined;
}

type ExportDownloadPath =
  | "/api/v1/ad_explorer/export/{filename}"
  | "/api/v1/audience_explorer/export/{filename}"
  | "/api/v1/campaign_effectiveness/export/{filename}"
  | "/api/v1/meta_audiences/export/{filename}"
  | "/api/v1/research_studio/export/{filename}";

/**
 * Fetch bytes for a completed async export. Passes ``storage_key`` when the task
 * result includes it so duplicate display names resolve to the correct object.
 */
export async function fetchExportDownload(
  path: ExportDownloadPath,
  file: ExportFileEntry,
  signal: AbortSignal,
): Promise<ArrayBuffer | undefined> {
  const { default: client } = await import("@/api/openapi-ts");
  const query = exportFileDownloadQuery(file);
  const { data } = await client.GET(path, {
    params: {
      path: { filename: file.filename },
      ...(query ? { query } : {}),
    } as never,
    parseAs: "arrayBuffer",
    signal,
  });
  return data as ArrayBuffer | undefined;
}

type CsvHeaderMapping = {
  label: string;
  key: string;
};

export type CsvExportOptions = {
  data: Record<string, unknown>[];
  filename?: string;
  delimiter?: string;
  headers?: string[] | CsvHeaderMapping[];
  /** Backend feature key (e.g. "cable_ranker") stored with the file record. */
  feature: string;
  /** Optional extra metadata stored with the file record. */
  meta_data?: Record<string, unknown>;
  /** When false, skip persisting to the Download Center. Defaults to true. */
  persist?: boolean;
};

function formatCsvFilename(filename?: string): string {
  const base = filename ?? "export.csv";
  return /csv$/i.test(base) ? base : `${base}.csv`;
}

function buildCsvString({
  data,
  delimiter = ",",
  headers,
}: Pick<CsvExportOptions, "data" | "delimiter" | "headers">): string {
  if (data.length === 0) {
    return headers
      ? headers
          .map((header) => (typeof header === "string" ? header : header.label))
          .join(delimiter)
      : "";
  }
  return csvGenerate(data, headers, delimiter);
}

function buildCsvBlob(
  options: Pick<CsvExportOptions, "data" | "delimiter" | "headers">,
): Blob {
  const csvAsString = buildCsvString(options);
  return new Blob(["\ufeff", csvAsString], { type: CSV_MIME_TYPE });
}

/**
 * Trigger a browser download for an already-fetched export payload. Centralizes
 * the blob + anchor + object-URL dance that every export feature used to
 * reimplement.
 */
export function downloadFile(
  filename: string,
  data: ArrayBuffer,
  mimetype?: string,
): void {
  const blob = new Blob([data], {
    type: mimetype || mimeTypeForFilename(filename),
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

async function persistCsvExport(
  blob: Blob,
  filename: string,
  feature: string,
  meta_data?: Record<string, unknown>,
): Promise<void> {
  const { useFileStore } = await import("@/stores/FileStore");
  const fileStore = useFileStore();
  await fileStore.uploadExport(blob, filename, feature, meta_data);
}

/**
 * Build and download a CSV in the browser, then fire-and-forget a copy to the
 * Download Center via POST /files. Matches json-to-csv-export output (BOM,
 * delimiter, headers) so persisted files match what the user downloaded.
 */
export function exportCsv(options: CsvExportOptions): void {
  const filename = formatCsvFilename(options.filename);
  const blob = buildCsvBlob(options);

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  if (options.persist === false) {
    return;
  }

  void persistCsvExport(
    blob,
    filename,
    options.feature,
    options.meta_data,
  ).catch((error) => {
    console.error("Failed to persist CSV export", error);
  });
}
